import {
  getJobCardsPartnerControllerGetJobCardQueryKey,
  getJobCardsPartnerControllerGetJobCardQueryOptions,
  getJobCardsPartnerControllerListJobCardEventsQueryKey,
  getJobCardsPartnerControllerListJobCardEventsQueryOptions,
  getJobCardsPartnerControllerListJobCardsQueryKey,
  getJobCardsPartnerControllerListJobCardsQueryOptions,
  type JobCardsPartnerControllerListJobCardsParams,
} from '@artic/api-client';
import { keepPreviousData } from '@tanstack/react-query';

/**
 * Query-key prefix for every job-cards read. The realtime hook invalidates
 * through these helpers rather than hand-built arrays so the keys can't drift
 * from the generated ones.
 */
export const JOB_CARDS_KEY = 'job-cards';

const LIST_STALE_TIME = 30_000;
const DETAIL_STALE_TIME = 15_000;
/** Events are append-only and drive the timeline — keep them fresher. */
const EVENTS_STALE_TIME = 10_000;

/** Job Cards Index: server-paginated, filtered and sorted. */
export function jobCardsListQueryOptions(
  partnerId: string | undefined,
  params: JobCardsPartnerControllerListJobCardsParams,
) {
  return getJobCardsPartnerControllerListJobCardsQueryOptions(partnerId ?? '', params, {
    query: {
      enabled: !!partnerId,
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE_TIME,
    },
  });
}

/** Job Card Detail. */
export function jobCardDetailQueryOptions(
  partnerId: string | undefined,
  jobCardId: string | undefined,
) {
  return getJobCardsPartnerControllerGetJobCardQueryOptions(partnerId ?? '', jobCardId ?? '', {
    query: {
      enabled: !!partnerId && !!jobCardId,
      placeholderData: keepPreviousData,
      staleTime: DETAIL_STALE_TIME,
    },
  });
}

/** Event history backing both the Timeline and Table tabs. */
export function jobCardEventsQueryOptions(
  partnerId: string | undefined,
  jobCardId: string | undefined,
) {
  return getJobCardsPartnerControllerListJobCardEventsQueryOptions(
    partnerId ?? '',
    jobCardId ?? '',
    undefined,
    {
      query: {
        enabled: !!partnerId && !!jobCardId,
        placeholderData: keepPreviousData,
        staleTime: EVENTS_STALE_TIME,
      },
    },
  );
}

/**
 * Key builders re-exported for cache invalidation. The list key is truncated to
 * its URL prefix so every filter/sort/page variant is invalidated together —
 * a realtime signal makes all of them stale, not just the one on screen.
 */
export const jobCardsQueryKeys = {
  listPrefix: (partnerId: string) =>
    getJobCardsPartnerControllerListJobCardsQueryKey(partnerId, undefined).slice(0, 1),
  detail: (partnerId: string, jobCardId: string) =>
    getJobCardsPartnerControllerGetJobCardQueryKey(partnerId, jobCardId),
  events: (partnerId: string, jobCardId: string) =>
    getJobCardsPartnerControllerListJobCardEventsQueryKey(partnerId, jobCardId, undefined),
};
